import { ref, computed } from 'vue';

// --- Singleton state (shared across all components) ---
const audioElement = ref(null);
const currentSurah = ref(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const onNextHandler = ref(null);
const onPrevHandler = ref(null);
const onEndedHandler = ref(null);

// Helper to get or create the single Audio instance
function getAudio() {
    if (!audioElement.value) {
        const audio = new Audio();
        audio.crossOrigin = 'anonymous';

        audio.addEventListener('timeupdate', () => {
            currentTime.value = audio.currentTime;
            duration.value = audio.duration || 0;
            if ('mediaSession' in navigator) {
                // Update position state for professional lock screen seekbar
                try {
                    navigator.mediaSession.setPositionState({
                        duration: audio.duration || 0,
                        playbackRate: audio.playbackRate,
                        position: audio.currentTime
                    });
                } catch (e) { /* Fallback */ }
            }
        });

        audio.addEventListener('durationchange', () => {
            duration.value = audio.duration || 0;
        });

        audio.addEventListener('ended', () => {
            isPlaying.value = false;
            if (onEndedHandler.value) onEndedHandler.value();
        });

        audio.addEventListener('play', () => {
            isPlaying.value = true;
            if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'playing';
        });

        audio.addEventListener('pause', () => {
            isPlaying.value = false;
            if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'paused';
        });

        audio.addEventListener('error', (e) => {
            console.error('AudioPlayer error:', e);
            isPlaying.value = false;
            if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'none';
        });

        audioElement.value = audio;
    }
    return audioElement.value;
}

// --- Public composable ---
export function useAudioPlayer() {
    const progress = computed(() => {
        if (!duration.value) return 0;
        return (currentTime.value / duration.value) * 100;
    });

    /**
     * Update system media session metadata (for lock screen/notifications)
     */
    function updateMediaMetadata(surah) {
        if ('mediaSession' in navigator && surah) {
            // Helper to get real name (matching the component logic)
            const surahNames = {
                1: "الفاتحة", 2: "البقرة", 3: "آل عمران", 4: "النساء", 5: "المائدة", 6: "الأنعام", 7: "الأعراف", 8: "الأنفال", 9: "التوبة", 10: "يونس",
                11: "هود", 12: "يوسف", 13: "الرعد", 14: "إبراهيم", 15: "الحجر", 16: "النحل", 17: "الإسراء", 18: "الكهف", 19: "مريم", 20: "طه",
                21: "الأنبياء", 22: "الحج", 23: "المؤمنون", 24: "النور", 25: "الفرقان", 26: "الشعراء", 27: "النمل", 28: "القصص", 29: "العنكبوت", 30: "الروم",
                31: "لقمان", 32: "السجدة", 33: "الأحزاب", 34: "سبأ", 35: "فاطر", 36: "يس", 37: "الصافات", 38: "ص", 39: "الزمر", 40: "غافر",
                41: "فصلت", 42: "الشورى", 43: "الزخرف", 44: "الدخان", 45: "الجاثية", 46: "الأحقاف", 47: "محمد", 48: "الفتح", 49: "الحجرات", 50: "ق",
                51: "الذاريات", 52: "الطور", 53: "النجم", 54: "القمر", 55: "الرحمن", 56: "الواقعة", 57: "الحديد", 58: "المجادلة", 59: "الحشر", 60: "الممتحنة",
                61: "الصف", 62: "الجمعـة", 63: "المنافقون", 64: "التغابن", 65: "الطلاق", 66: "التحريم", 67: "الملك", 68: "القلم", 69: "الحاقة", 70: "المعارج",
                71: "نوح", 72: "الجن", 73: "المزمل", 74: "المدثر", 75: "القيامة", 76: "الإنسان", 77: "المرسلات", 78: "النبأ", 79: "النازعات", 80: "عبس",
                81: "التكوير", 82: "الانفطار", 83: "المطـفـفـين", 84: "الانشقاق", 85: "البروج", 86: "الطارق", 87: "الأعلى", 88: "الغاشية", 89: "الفجر", 90: "البلد",
                91: "الشمس", 92: "الليل", 93: "الضحى", 94: "الشرح", 95: "التين", 96: "العلق", 97: "القدر", 98: "البينة", 99: "الزلزلة", 100: "العاديات",
                101: "القارعة", 102: "التكاثر", 103: "العصر", 104: "الهمزة", 105: "الفيل", 106: "قريش", 107: "الماعون", 108: "الكوثر", 109: "الكافرون", 110: "النصر",
                111: "المسد", 112: "الإخلاص", 113: "الفلق", 114: "الناس"
            };
            const realName = surahNames[parseInt(surah.number || surah.id)] || surah.name;

            navigator.mediaSession.metadata = new MediaMetadata({
                title: realName,
                artist: 'الرحيمة - القرآن الكريم',
                album: 'تلاوات خاشعة',
                artwork: [
                    { src: 'https://alrihema.com/og-image.jpg', sizes: '512x512', type: 'image/jpeg' }
                ]
            });

            // Set Action Handlers
            navigator.mediaSession.setActionHandler('play', () => {
                getAudio().play().catch(console.error);
            });
            navigator.mediaSession.setActionHandler('pause', () => {
                getAudio().pause();
            });
            navigator.mediaSession.setActionHandler('seekbackward', (details) => {
                seekBackward(details.seekOffset || 10);
            });
            navigator.mediaSession.setActionHandler('seekforward', (details) => {
                seekForward(details.seekOffset || 10);
            });
            
            // ✅ Swap for Arabic RTL navigation: 
            // - Right-pointing button (System Next) -> Previous Surah
            // - Left-pointing button (System Prev) -> Next Surah
            navigator.mediaSession.setActionHandler('previoustrack', onNextHandler.value ? () => onNextHandler.value() : null);
            navigator.mediaSession.setActionHandler('nexttrack', onPrevHandler.value ? () => onPrevHandler.value() : null);

            // Allow seeking from system slider
            navigator.mediaSession.setActionHandler('seekto', (details) => {
                if (details.fastSeek && 'fastSeek' in getAudio()) {
                    getAudio().fastSeek(details.seekTime);
                } else {
                    getAudio().currentTime = details.seekTime;
                }
            });
        }
    }

    /**
     * Play a surah. If the same surah is already loaded, toggle play/pause.
     * @param {Object} surah - Must have a `.url` property and `.id`.
     */
    async function playSurah(surah) {
        if (!surah || !surah.url) return;

        const audio = getAudio();

        // Toggle if same surah (comparing by ID or URL)
        const isSameSurah = currentSurah.value && (currentSurah.value.id === surah.id || currentSurah.value.url === surah.url);
        const hasSource = audio.src && surah.url && audio.src.includes(surah.url);

        if (isSameSurah && hasSource) {
            try {
                if (audio.paused) {
                    await audio.play();
                    isPlaying.value = true;
                } else {
                    audio.pause();
                    isPlaying.value = false;
                }
                updateMediaMetadata(surah);
                return;
            } catch (playErr) {
                console.error('Failed to toggle play/pause:', playErr);
            }
        }

        // Load new surah
        audio.pause();
        currentSurah.value = surah;
        isPlaying.value = false;
        currentTime.value = 0;
        duration.value = 0;

        // Build URL
        const apiBaseUrl = process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000';
        let audioUrl = surah.url.startsWith('http') ? surah.url : `/api${surah.url}`;

        if (process.env.NODE_ENV !== 'production' && !audioUrl.startsWith('http')) {
            audioUrl = `${apiBaseUrl}${audioUrl}`;
        }

        audio.src = audioUrl;
        audio.load();

        try {
            await audio.play();
            isPlaying.value = true;
            updateMediaMetadata(surah);
        } catch (err) {
            console.error('Playback failed:', err);
            isPlaying.value = false;
            throw err;
        }
    }

    /**
     * Seek to a specific time in seconds.
     */
    function seekTo(seconds) {
        const audio = getAudio();
        if (audio.readyState >= 1) {
            audio.currentTime = Math.max(0, Math.min(seconds, audio.duration));
        }
    }

    function seekForward(seconds = 10) {
        seekTo(currentTime.value + seconds);
    }

    function seekBackward(seconds = 10) {
        seekTo(currentTime.value - seconds);
    }

    /**
     * Seek to a specific percentage (0-100).
     */
    function seekToPercentage(percentage) {
        if (!duration.value) return;
        const seconds = (percentage / 100) * duration.value;
        seekTo(seconds);
    }

    /**
     * Format seconds to mm:ss string.
     */
    function formatTime(seconds) {
        if (seconds === undefined || seconds === null || isNaN(seconds)) return '0:00';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    }

    /**
     * Stop playback completely.
     */
    function stop() {
        if (!audioElement.value) return;
        audioElement.value.pause();
        audioElement.value.currentTime = 0;
        isPlaying.value = false;
        currentSurah.value = null;
        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'none';
        }
    }

    function setHandlers(next = null, prev = null, ended = null) {
        if (next !== null) onNextHandler.value = next;
        if (prev !== null) onPrevHandler.value = prev;
        if (ended !== null) onEndedHandler.value = ended;
        // Reinforce the handlers in MediaSession
        if ('mediaSession' in navigator && currentSurah.value) {
            updateMediaMetadata(currentSurah.value);
        }
    }

    return {
        audioElement,
        currentSurah,
        isPlaying,
        currentTime,
        duration,
        progress,
        playSurah,
        seekTo,
        seekForward,
        seekBackward,
        seekToPercentage,
        formatTime,
        updateMediaMetadata,
        stop,
        setHandlers
    };
}
